// Calendar.tsx
import { useFormUtils } from "@/app/hooks/useFormUtils";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { Dialog, Transition } from "@headlessui/react";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { addDays, format, getDate, getMonth, getYear, isToday, parse, subDays } from "date-fns";
import React, { useEffect, useState } from "react";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { IPWLCalendar } from "../../interface";
import { TbEdit } from "react-icons/tb";
import { FiCalendar } from "react-icons/fi";

type ByDateOptionFilter = "today" | "yesterday" | "tomorrow" | "custom";

const EMRCalendar: React.FC<IPWLCalendar> = ({
  selectedDate = new Date(),
  onSelectDate,
  errorMessages,
  showLeftNav,
  withClear,
  fieldError,
  showTypedErrors,
  allowPastDaySelection,
  // blockFutureDaySelection,
  className,
  register,
  showFutureYears,
  label,
  showCalendarIcon,
  defaultSelectedDate,
  ...props
}) => {
  const currentTodayDate = new Date(Date.now());
  const { deduceFormFieldErrors } = useFormUtils();
  const constants = useStringConstants();
  const [selectedByDateOption, setSelectedByDateOption] = useState<ByDateOptionFilter>("custom");
  const [currentSelectedDate, setCurrentSelectedDate] = useState<Date>(currentTodayDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState<number | null>(getYear(selectedDate));
  const [isOpen, setIsOpen] = useState(false);

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const byDateFilterOptions = [
    {
      title: "Today",
      id: "today",
      callbackFunc: () => {
        setCurrentSelectedDate(currentTodayDate);
        setCurrentMonth(currentTodayDate);
        setSelectedYear(getYear(currentTodayDate));
        setSelectedByDateOption("today");
        onSelectDate(currentTodayDate);
        toggleCalendar();
      },
    },
    {
      title: "Tomorrow",
      id: "tomorrow",
      callbackFunc: () => {
        const tomorrowDate = addDays(currentTodayDate, 1);
        setCurrentSelectedDate(tomorrowDate);
        setCurrentMonth(tomorrowDate);
        setSelectedYear(getYear(tomorrowDate));
        setSelectedByDateOption("tomorrow");
        onSelectDate(tomorrowDate);
        toggleCalendar();
      },
    },
    {
      title: "Yesterday",
      id: "yesterday",
      callbackFunc: () => {
        const yesterdayDate = subDays(currentTodayDate, 1);
        setCurrentSelectedDate(yesterdayDate);
        setCurrentMonth(yesterdayDate);
        setSelectedYear(getYear(yesterdayDate));
        setSelectedByDateOption("yesterday");
        onSelectDate(yesterdayDate);
        toggleCalendar();
      },
    },
  ];

  let calendarYears = allowPastDaySelection && allowPastDaySelection ? getYear(new Date()) - 1900 : 1;

  calendarYears = showFutureYears ? calendarYears + 10 : calendarYears;

  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center mb-2">
        <div className="relative">
          <div className="flex justify-start items-center mt-2 w-full overflow-hidden">
            <div className="p-2">
              <select
                className="pr-2 border  rounded w-28 text-xs outline-none focus:outline-none"
                value={selectedYear ?? ""}
                onChange={(e) => {
                  const year = parseInt(e.target.value, 10);
                  setSelectedYear(year === -1 ? null : year);
                  if (selectedYear < year) {
                    //
                  } else if (allowPastDaySelection) {
                    //
                  } else {
                    setCurrentMonth(currentTodayDate);
                    onSelectDate(currentTodayDate);
                  }
                }}
              >
                <option value={-1}>Select Year</option>
                {Array.from({ length: calendarYears }, (_, index) => {
                  if (showFutureYears) {
                    return getYear(new Date()) + index;
                  } else {
                    return getYear(new Date()) - index;
                  }
                }).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="p-2">
              <select
                className="pr-2 border  rounded w-28 text-xs  focus:outline-none"
                value={getMonth(currentMonth)}
                // disabled={props.disableDatesBeforeDate&&selectedYear===getYear(currentTodayDate)}
                onChange={(e) => {
                  const newMonth = parseInt(e.target.value, 10);
                  setCurrentMonth((prev) => new Date(selectedYear ?? getYear(prev), newMonth, 1));
                }}
              >
                <option value={-1}>Select Month</option>
                {Array.from({ length: 12 }, (_, index) => index).map((month) => {
                  return props.disabledFutureDateYearMonthSelection &&
                    currentTodayDate.getFullYear() === selectedYear ? (
                    currentTodayDate?.getMonth() >= month && (
                      <option key={month} value={month}>
                        {format(new Date(selectedYear ?? getYear(new Date()), month, 1), "MMMM")}
                      </option>
                    )
                  ) : selectedYear === getYear(currentTodayDate) && props?.disablePastMonthSelection ? (
                    month >= getMonth(currentTodayDate) && (
                      <option key={month} value={month}>
                        {format(new Date(selectedYear ?? getYear(new Date()), month, 1), "MMMM")}
                      </option>
                    )
                  ) : (
                    <option key={month} value={month}>
                      {format(new Date(selectedYear ?? getYear(new Date()), month, 1), "MMMM")}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
      <div className="gap-2 border-gray-500 grid grid-cols-7 mb-2 border-b text-center">
        {days.map((day) => (
          <div key={day} className="p-2 rounded text-xs">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);

  const isValid = Object.keys(reducedErrorMessages).length === 0;

  const handleCheckboxSelect = (selectedDate: Date) => {
    if (isToday(selectedDate)) {
      setSelectedByDateOption("today");
    } else if (
      format(subDays(currentTodayDate, 1), constants.viewDOBFormat) === format(selectedDate, constants.viewDOBFormat)
    ) {
      setSelectedByDateOption("yesterday");
    } else if (
      format(addDays(currentTodayDate, 1), constants?.viewDOBFormat) === format(selectedDate, constants?.viewDOBFormat)
    ) {
      setSelectedByDateOption("tomorrow");
    } else {
      setSelectedByDateOption("custom");
    }
  };

  const renderCells = () => {
    const cells = [];
    let day = 1;

    // Calculate the first day of the current month using the selected year
    const firstDayOfMonth = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth(), 1);
    // Calculate the last day of the current month
    const lastDayOfMonth = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth() + 1, 0);

    // Calculate the number of days to display from the previous month
    const daysFromPrevMonth = firstDayOfMonth.getDay();

    // Render cells for the previous month
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const prevMonthDate = subDays(firstDayOfMonth, daysFromPrevMonth - i);
      cells.push(
        <div key={`prev-${i}`} className="py-3 rounded text-center text-gray-400 text-xs cursor-not-allowed">
          {getDate(prevMonthDate)}
        </div>,
      );
    }

    // Render cells for the current month
    while (day <= lastDayOfMonth.getDate()) {
      const currentDate = new Date(selectedYear ?? new Date().getFullYear(), currentMonth.getMonth(), day);
      const currentSelectedMonth = currentMonth.getMonth();
      const disabledBeforeMonth = getMonth(parse(props?.disableDatesBeforeDate, "yyyy-MM-dd", new Date()));
      const disabledAfterMonth = getMonth(parse(props?.disableDatesAfterDate, "yyyy-MM-dd", new Date()));

      const disabledAfterYear = getYear(parse(props?.disableDatesAfterDate, "yyyy-MM-dd", new Date()));
      const disabledBeforeDate = props?.disableDatesBeforeDate
        ? getDate(parse(props?.disableDatesBeforeDate, "yyyy-MM-dd", new Date()))
        : 0;

      const disabledAfterDate = props?.disableDatesAfterDate
        ? getDate(parse(props?.disableDatesAfterDate, "yyyy-MM-dd", new Date()))
        : 32;

      cells.push(
        <div
          key={`current-${day}`}
          onClick={() => {
            const isBeforeDisabledDate = currentDate < parse(props?.disableDatesBeforeDate, "yyyy-MM-dd", new Date());
            const isAfterDisabledDate = currentDate > parse(props?.disableDatesAfterDate, "yyyy-MM-dd", new Date());

            if (isBeforeDisabledDate || isAfterDisabledDate) return;

            onSelectDate(currentDate);
            handleCheckboxSelect(currentDate);
            setCurrentSelectedDate(currentDate);
            toggleCalendar();
          }}
          className={clsx(
            "py-3 rounded text-center text-xs",
            currentDate < parse(props?.disableDatesBeforeDate, "yyyy-MM-dd", new Date()) &&
            "cursor-not-allowed bg-red-300",
            currentDate > parse(props?.disableDatesAfterDate, "yyyy-MM-dd", new Date()) &&
            "cursor-not-allowed bg-red-300",
            currentDate?.toDateString() === selectedDate?.toDateString() && "border border-black text-black",
            currentDate?.toDateString() === currentSelectedDate?.toDateString() && "bg-black shadow-md text-white",
          )}
        >
          {day}
        </div>,
      );
      day++;
    }

    // Render cells for the next month to fill the grid
    const remainingDays = 7 - (cells.length % 7);
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDate = addDays(lastDayOfMonth, i);
      cells.push(
        <div key={`next-${i}`} className="py-3 rounded text-center text-gray-400 text-xs cursor-not-allowed">
          {getDate(nextMonthDate)}
        </div>,
      );
    }
    return (
      <div className="gap-2 grid grid-cols-7">
        {cells.map((cell) => {
          return <span>{cell}</span>;
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!defaultSelectedDate) {
      setCurrentSelectedDate(null);
    }
    if (defaultSelectedDate) {
      const parsedDate = parse(defaultSelectedDate, "yyyy-MM-dd", new Date());
      setCurrentSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
      setSelectedYear(getYear(parsedDate));
    }
  }, [defaultSelectedDate]);

  return (
    <>
      <div className="flex">
        <div className="relative rounded-md">
          {label && <span className="-top-4 left-1 absolute text-[#808DA0] text-xs">{label}</span>}

          <input hidden type="text" {...register} />
          <FiCalendar
            className="w-6 h-6 font-medium cursor-pointer"
            onClick={props?.disabled ? () => { } : toggleCalendar}
          />
        </div>
      </div>
      {showTypedErrors && <FormFieldErrors errors={reducedErrorMessages} />}
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="z-50 fixed inset-0 overflow-y-auto" onClose={toggleCalendar} static>
          <div className="flex flex-col justify-center items-center px-4 w-full min-h-screen text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
            </Transition.Child>

            {/* This is the modal container */}
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <div
                className={clsx(
                  "bg-white opacity-100 shadow-xl rounded max-w-2xl text-left transform transition-all translate-y-0 overflow-hidden",
                  !showLeftNav ? "" : "grid grid-cols-6  p-6 w-full ",
                )}
              >
                {showLeftNav && (
                  <div className="col-span-2">
                    <p className="font-semibold text-left">By Date</p>
                    <div className="flex flex-col justify-start items-start pt-6">
                      {byDateFilterOptions.map((option) => {
                        return (
                          <div className="relative flex items-start">
                            <div className="flex items-center h-6">
                              <input
                                id="today"
                                checked={selectedByDateOption === option.id}
                                onChange={() => {
                                  setSelectedByDateOption(option.id as ByDateOptionFilter);
                                  option.callbackFunc();
                                }}
                                aria-describedby="offers-description"
                                name="offers"
                                type="checkbox"
                                className="border-gray-300 rounded focus:ring-black w-4 h-4 text-black"
                              />
                            </div>
                            <div className="ml-3 text-sm leading-6">
                              <label
                                htmlFor="offers"
                                className={clsx(
                                  "font-normal text-gray-700 text-xs",
                                  selectedByDateOption === option.id && "text-gray-800 font-semibold",
                                )}
                              >
                                {option.title}
                              </label>{" "}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="col-span-4">
                  <div className="inline-block bg-white shadow-xl p-6 rounded w-full max-w-md text-left transform transition-all overflow-hidden align-middle">
                    {renderHeader()}
                    {renderDays()}
                    {renderCells()}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default EMRCalendar;
